// AclEditor (DESIGN §3.4.10): Rechte eines Ordners, einer Datei oder eines Eintrags im Dialog `m`.
// Switch „Inherit from <Ordner>“ → Einträge (Zeile 52: Avatar/Icon 32, Name, Herkunft bei geerbten,
// Rolle Viewer/Editor/Manager, Entfernen) → Hinzufügen per Suche (Loader-Prop) → Vorschau „Who can
// see this“ → Warnung bei „Company“ → Save/Cancel. Der letzte Manager ist nicht entfernbar; während
// des Speicherns steht „Updating search access…“.
import { useMemo, useRef, useState, type ReactNode } from 'react';
import {
  BuildingsIcon,
  GlobeSimpleIcon,
  IdentificationBadgeIcon,
  UsersThreeIcon,
  WarningIcon,
  XIcon,
} from '@phosphor-icons/react';
import {
  ACL_ROLES,
  aclKey,
  addSubject,
  isLastManager,
  removeEntry,
  setRole,
  type AclEntry,
  type AclRole,
  type AclSubject,
  type AclSubjectKind,
} from './_lib/acl';
import type { OptionsLoader } from './_lib/useAsyncOptions';
import { Button } from './Button';
import { Combobox, type ComboboxOption } from './Combobox';
import { Dialog, DialogContent } from './Dialog';
import { IconButton } from './IconButton';
import { Select } from './Select';
import { Switch } from './Switch';
import { Tooltip } from './Tooltip';
import { Avatar, cn, formatNumber, Spinner } from '../basis/kern';

export type { AclEntry, AclRole, AclSubject, AclSubjectKind } from './_lib/acl';

const KIND_ICON: Record<Exclude<AclSubjectKind, 'person'>, ReactNode> = {
  company: <GlobeSimpleIcon />,
  department: <BuildingsIcon />,
  team: <UsersThreeIcon />,
  role: <IdentificationBadgeIcon />,
};

const NO_ENTRIES: AclEntry[] = [];

export const LAST_MANAGER_HINT = 'A file needs at least one manager.';

export interface AclAudience {
  count: number;
  /** Avatar-Stapel der ersten Personen (AvatarStack aus anzeige, als Slot). */
  avatars?: ReactNode;
}

export interface AclSaveResult {
  inherit: boolean;
  entries: AclEntry[];
}

export interface AclEditorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** z. B. „Share “Travel_Policy_2026.pdf”“. */
  title: ReactNode;
  /** Name des Elternordners für „Inherit from …“; ohne Namen kein Schalter (Wurzel). */
  parentName?: string;
  inherit?: boolean;
  /** Geerbte Einträge (angezeigt, solange geerbt wird). */
  inheritedEntries?: AclEntry[];
  /** Eigene Einträge. */
  entries: AclEntry[];
  /** Suche über Personen, Teams, Abteilungen und Rollen. */
  loadSubjects: OptionsLoader<AclSubject>;
  /** Zielgruppe der wirksamen Einträge, z. B. `{ count: 65 }`. */
  describeAudience?: (effective: AclEntry[]) => AclAudience | null;
  onShowAudience?: () => void;
  /** Warnhinweis, sobald „Company“ Zugriff hat (z. B. im Abteilungsordner). */
  companyWarning?: string;
  /** Speichern; ein abgelehntes Promise lässt den Dialog offen. */
  onSave: (result: AclSaveResult) => void | Promise<void>;
}

/** Öffnet bei jedem Öffnen mit frischem Entwurf aus den Props. */
export function AclEditor(props: AclEditorProps) {
  const [prevOpen, setPrevOpen] = useState(props.open);
  const [session, setSession] = useState(0);
  if (props.open !== prevOpen) {
    setPrevOpen(props.open);
    if (props.open) setSession((n) => n + 1);
  }
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <AclEditorContent key={session} {...props} />
    </Dialog>
  );
}

function SubjectIcon({ entry }: { entry: AclSubject }) {
  if (entry.kind === 'person')
    return <Avatar name={entry.name} id={entry.id} size={32} decorative />;
  return (
    <span
      aria-hidden="true"
      className="inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-icon-circle text-fg-secondary [&>svg]:size-4"
    >
      {KIND_ICON[entry.kind]}
    </span>
  );
}

function AclEditorContent({
  onOpenChange,
  title,
  parentName,
  inherit: inheritProp = Boolean(parentName),
  inheritedEntries = NO_ENTRIES,
  entries: entriesProp,
  loadSubjects,
  describeAudience,
  onShowAudience,
  companyWarning,
  onSave,
}: AclEditorProps) {
  const [inherit, setInherit] = useState(inheritProp && Boolean(parentName));
  const [entries, setEntries] = useState(entriesProp);
  const [saving, setSaving] = useState(false);
  const seen = useRef(new Map<string, AclSubject>());

  const effective = useMemo(
    () => (inherit ? [...inheritedEntries, ...entries] : entries),
    [inherit, inheritedEntries, entries],
  );
  const audience = describeAudience?.(effective) ?? null;
  const hasCompany = effective.some((entry) => entry.kind === 'company');

  const loader = useMemo<OptionsLoader<ComboboxOption>>(
    () => async (query, signal) => {
      const own = new Set(entries.map(aclKey));
      const subjects = await loadSubjects(query, signal);
      return subjects.map((subject) => {
        const key = aclKey(subject);
        seen.current.set(key, subject);
        return {
          value: key,
          label: subject.name,
          description: subject.description,
          leading: <SubjectIcon entry={subject} />,
          disabled: own.has(key),
        };
      });
    },
    // Neu bei geänderten Einträgen, damit bereits Hinzugefügte gesperrt erscheinen.
    [loadSubjects, entries],
  );

  const save = async () => {
    setSaving(true);
    try {
      await onSave({ inherit, entries });
      onOpenChange(false);
    } catch {
      // Der Aufrufer meldet den Fehler (Toast); der Entwurf bleibt erhalten.
    } finally {
      setSaving(false);
    }
  };

  const footer = (
    <>
      {saving ? (
        <span
          role="status"
          className="mr-auto inline-flex items-center gap-2 self-center text-label text-fg-secondary"
        >
          <Spinner size={14} />
          Updating search access…
        </span>
      ) : null}
      <Button
        variant="secondary"
        surface="card"
        disabled={saving}
        onClick={() => onOpenChange(false)}
      >
        Cancel
      </Button>
      <Button variant="primary" loading={saving} onClick={() => void save()}>
        Save
      </Button>
    </>
  );

  return (
    <DialogContent title={title} size="m" footer={footer}>
      <div className="flex flex-col gap-4">
        {parentName ? (
          <Switch
            checked={inherit}
            onCheckedChange={setInherit}
            label={`Inherit from ${parentName}`}
            description={
              inherit
                ? 'People with access to the folder keep it here.'
                : 'Only the people below have access.'
            }
          />
        ) : null}
        <ul aria-label="People and groups with access" className="flex flex-col">
          {effective.map((entry) => {
            const key = aclKey(entry);
            const inherited = Boolean(entry.inheritedFrom);
            const lastManager = isLastManager(entry, effective);
            return (
              <li
                key={`${inherited ? 'i' : 'o'}-${key}`}
                className="flex min-h-13 items-center gap-3 py-1.5"
              >
                <SubjectIcon entry={entry} />
                <span className="flex min-w-0 flex-1 flex-col">
                  <span
                    className={cn(
                      'truncate text-body',
                      inherited ? 'text-fg-secondary' : 'text-fg',
                    )}
                  >
                    {entry.name}
                  </span>
                  {inherited ? (
                    <span className="truncate text-small text-fg-tertiary">
                      from {entry.inheritedFrom}
                    </span>
                  ) : null}
                </span>
                <Select
                  variant="pill"
                  aria-label={`Access for ${entry.name}`}
                  value={entry.role}
                  disabled={inherited}
                  onValueChange={(role) =>
                    setEntries((list) => setRole(list, key, role as AclRole))
                  }
                  className="h-8 px-3 text-label"
                  options={ACL_ROLES.map((role) => ({
                    ...role,
                    disabled: lastManager && role.value !== 'manager',
                  }))}
                />
                {inherited ? (
                  <span className="size-8 shrink-0" aria-hidden="true" />
                ) : lastManager ? (
                  <Tooltip content={LAST_MANAGER_HINT}>
                    <IconButton
                      label={`Remove ${entry.name}`}
                      icon={<XIcon />}
                      variant="ghost"
                      surface="card"
                      size="sm"
                      tooltip={false}
                      aria-disabled
                    />
                  </Tooltip>
                ) : (
                  <IconButton
                    label={`Remove ${entry.name}`}
                    icon={<XIcon />}
                    variant="ghost"
                    surface="card"
                    size="sm"
                    onClick={() => setEntries((list) => removeEntry(list, key))}
                  />
                )}
              </li>
            );
          })}
        </ul>
        <Combobox
          aria-label="Add people, teams, departments or roles"
          placeholder="Add people, teams, departments or roles"
          value={null}
          loadOptions={loader}
          onValueChange={(key) => {
            const subject = key ? seen.current.get(key) : undefined;
            if (subject) setEntries((list) => addSubject(list, subject));
          }}
        />
        {audience ? (
          <div className="flex items-center gap-3 text-label text-fg-secondary">
            {audience.avatars}
            <span>
              Who can see this:{' '}
              <span className="text-fg tabular-nums">{formatNumber(audience.count)} people</span>
            </span>
            {onShowAudience ? (
              <Button variant="link" size="sm" onClick={onShowAudience} className="ml-auto">
                Show list
              </Button>
            ) : null}
          </div>
        ) : null}
        {hasCompany && companyWarning ? (
          <div
            role="note"
            className="flex items-start gap-2 rounded-lg bg-warning-soft px-3 py-2.5 text-label text-fg"
          >
            <WarningIcon size={16} aria-hidden="true" className="mt-0.5 shrink-0 text-warning-fg" />
            {companyWarning}
          </div>
        ) : null}
      </div>
    </DialogContent>
  );
}
